/**
 * Wave-Viz 2：销售视角「距离升级还差 X」激励摘要条（V-FE-dashboard）
 *
 * 纯前端、零新增后端：复用两个现成端点的缓存（与现有 queryKey 严格对齐，不产生重复请求）：
 *   - GET /salary/my-incentive：与 MyIncentiveCard 同 key ['my-incentive']（TanStack 自动共享缓存）。
 *     距下一级还差：业绩差=Math.max(0, progress.perf.target-progress.perf.current)；
 *     单量差=Math.max(0, progress.deals.target-progress.deals.current)（即 MyIncentiveCard 已有算法）。
 *     isTop=true 时 progress=null → 显已最高职级。
 *   - GET /dashboard/sales：与 Dashboard.tsx 第102行 queryKey ['dashboard','sales-aux'] 严格一致，复用其缓存。
 *     data.funnel = byStage 聚合（本人 owner 名下按 current_stage 分组计数），取 L1/L2/L3 三档作储备。
 *     funnel 不补零档：缺档 find 不到 → 当 0。current_stage 精确匹配字符串，不前缀模糊。
 *
 * 仅 role==='sales' 显示（仿 MyIncentiveCard），挂工作台顶部，与中部 MyIncentiveCard 完整卡共存。
 * 一行扁平条样式参照 MyIncentiveCard 容器 token（--bg-2/--border/borderRadius 8），不动滚动链。
 */
import { useQuery } from '@tanstack/react-query';
import { $ } from '../../lib/api';
import { STAGE_SHORT } from '../ui';
import './incentive.css';

// 万/k 口径与 MyIncentiveCard 一致（复制，保持金额展示统一）
const yuan = (n: number) => {
  const v = Number(n || 0);
  return v >= 10000 ? `¥${(v / 10000).toFixed(v % 10000 === 0 ? 0 : 1)}万` : `¥${Math.round(v).toLocaleString()}`;
};

// 等级中文 → 徽章配色类（见习=灰/初级=蓝/中级=紫/高级=金渐变）
const gradeClass = (label: string) => {
  if (label === '高级') return 'inc-badge--senior';
  if (label === '中级') return 'inc-badge--mid';
  if (label === '初级') return 'inc-badge--junior';
  return 'inc-badge--trainee'; // 见习 + 兜底
};
const gradeIcon = (label: string) => {
  if (label === '高级') return '👑';
  if (label === '中级') return '🔷';
  if (label === '初级') return '🔹';
  return '🌱';
};
const clampPct = (n: number) => Math.max(0, Math.min(100, Math.round(Number(n) || 0)));

export function UpgradeIncentiveStrip() {
  const { data } = useQuery<any>({
    queryKey: ['my-incentive'],
    queryFn: () => $.get('/salary/my-incentive'),
    refetchInterval: 120_000,
  });
  // ★必须与 Dashboard.tsx ['dashboard','sales-aux'] 完全一致，复用缓存、不新增网络请求★
  const { data: salesKpi } = useQuery<any>({
    queryKey: ['dashboard', 'sales-aux'],
    queryFn: () => $.get('/dashboard/sales'),
  });

  if (!data) return null;
  const { gradeLabel, progress, upgradeGain, isTop, maintain } = data;

  // 储备：本人 owner 名下 L1/L2/L3 当前阶段计数（缺档当 0），合计
  const funnel: Array<{ stage: string; cnt: number }> = salesKpi?.funnel ?? [];
  const stageCnt = (st: string) =>
    funnel.filter((f) => f.stage === st).reduce((s, f) => s + Number(f.cnt ?? 0), 0);
  const n1 = stageCnt('L1');
  const n2 = stageCnt('L2');
  const n3 = stageCnt('L3');
  const reserveTotal = n1 + n2 + n3;

  // 升级紧迫：isTop → 已最高；否则 progress 存在 → 还差业绩/单量
  const perfGap = progress ? Math.max(0, (progress.perf.target ?? 0) - progress.perf.current) : 0;
  const dealGap = progress ? Math.max(0, (progress.deals.target ?? 0) - progress.deals.current) : 0;

  // 醒目升级进度条：取业绩/单量里【完成度更高的那条】作主进度（纯展示，不改数据口径）
  const perfPct = progress ? clampPct(progress.perf.pct) : 0;
  const dealPct = progress ? clampPct(progress.deals.pct) : 0;
  const leadIsPerf = perfPct >= dealPct;
  const leadPct = Math.max(perfPct, dealPct);
  const leadName = leadIsPerf ? '业绩' : '单量';
  const leadFillClass = leadPct >= 80 ? 'inc-prog-fill--green' : 'inc-prog-fill--blue';
  // 还差：跟着主进度那条走（业绩→¥差额；单量→N 单）
  const leadGapText = leadIsPerf ? yuan(perfGap) : `${dealGap} 单`;

  return (
    <div className="inc-strip">
      <span className={`inc-badge ${gradeClass(gradeLabel)}${gradeLabel === '高级' ? ' inc-badge-shine' : ''}`}>
        <span className="inc-badge-ico">{gradeIcon(gradeLabel)}</span>
        {gradeLabel}
      </span>

      {/* 左/中段：升级紧迫 + 主进度条 */}
      {isTop ? (
        <span className="inc-top" style={{ fontSize: 13 }}>
          <span className="inc-top-trophy" style={{ fontSize: 18 }}>🏆</span>
          <span>已是最高职级</span>
          {maintain && (
            <span className="inc-top-maintain">
              保持 月业绩 ≥{yuan(maintain.perf)} 或 单量 ≥{maintain.deals} 单 即稳级
            </span>
          )}
        </span>
      ) : progress ? (
        <span className="inc-strip-prog">
          <span className="inc-strip-gap">
            距「{upgradeGain?.nextGradeLabel}」还差&nbsp;
            <b className="inc-gap-strong">{leadGapText}</b>
            <span className="inc-gap-or">（{leadName}）</span>
          </span>
          <span className="inc-prog-track" title={`${leadName} ${leadPct}%`}>
            <span
              className={`inc-prog-fill ${leadFillClass}`}
              style={{ ['--inc-pct' as any]: `${leadPct}%` }}
            />
          </span>
          <b className="inc-prog-pct" style={{ color: leadPct >= 80 ? 'var(--success-text)' : 'var(--primary)' }}>
            {leadPct}%
          </b>
        </span>
      ) : null}

      {/* 右段：L1-L3 储备小徽章组（分档 + 合计）*/}
      <span className="inc-reserve inc-strip-spacer">
        <span className="inc-reserve-title">储备</span>
        <span className={`inc-chip${n1 > 0 ? '' : ' inc-chip--empty'}`}>{STAGE_SHORT['L1']} <b className="inc-chip-n">{n1}</b></span>
        <span className={`inc-chip${n2 > 0 ? '' : ' inc-chip--empty'}`}>{STAGE_SHORT['L2']} <b className="inc-chip-n">{n2}</b></span>
        <span className={`inc-chip${n3 > 0 ? '' : ' inc-chip--empty'}`}>{STAGE_SHORT['L3']} <b className="inc-chip-n">{n3}</b></span>
        <span className={`inc-chip inc-chip--total${reserveTotal > 0 ? '' : ' inc-chip--empty'}`}>合计 <b className="inc-chip-n">{reserveTotal}</b></span>
      </span>
    </div>
  );
}
